/**
 * CalendarShell — page wrapper for `/calendar`.
 *
 * Mirrors `DocumentsShell` — provides `<AppNav />`, the compliance
 * strap, and the max-w-6xl content region. Kept in a small helper
 * component so the actual `Calendar.jsx` page focuses on data logic
 * and view switching without repeating the layout chrome.
 */
import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import AppNav from "@/components/app/AppNav";
import { api } from "@/api/client";

export default function CalendarShell({ children, testId = "calendar-shell-root" }) {
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/site-settings")
      .then((res) => {
        if (!cancelled) setSiteSettings(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const disclaimer = siteSettings?.disclaimer_text;

  return (
    <div
      data-testid={testId}
      data-page-outer="calendar"
      className="min-h-screen text-v2-text bg-v2-background"
      style={{ background: "var(--v2-background)" }}
    >
      <AppNav />
      <main className="max-w-6xl mx-auto px-6 md:px-10 pt-5 pb-24">
        {disclaimer && (
          <details
            data-testid="calendar-site-disclaimer"
            className="rounded-xl px-3 py-2 text-[11px] text-v2-textMuted leading-relaxed"
            style={{
              background: "rgba(167,139,250,0.05)",
              border: "1px solid rgba(167,139,250,0.15)",
            }}
          >
            <summary className="flex items-center gap-2 cursor-pointer list-none">
              <Info className="w-3 h-3 text-v2-accent flex-shrink-0" />
              <span className="uppercase tracking-[0.14em] text-[10px] font-semibold text-v2-textMuted">
                Compliance notice
              </span>
              <span className="ml-1 text-v2-textMuted/70">— tap to expand</span>
            </summary>
            <p className="mt-2 pl-5">{disclaimer}</p>
          </details>
        )}
        {children}
      </main>
    </div>
  );
}
